import { Color } from '@persona-id/design-tokens';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change, formValueSelector, initialize, reduxForm } from 'redux-form';
import styled from 'styled-components';

import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import MatchRequirementPanel from 'dashboard/components-lib/MatchRequirementPanel';
import MatchRequirementsView from 'dashboard/components-lib/MatchRequirementsView';
import {
  MatchRequirementKeys,
  MatchRequirementProfile,
} from 'dashboard/models/match-requirement-profile';
import { ReportTemplateVersionCustomList } from 'dashboard/models/report-template-version';
import { MatchGroup, MatchRequirement } from 'dashboard/models/verification-check';
import { RootState } from 'dashboard/redux/store';
import { CountryCode } from 'lib/constants/locales';

const CUSTOM_LIST_CONFIG_MATCH_GROUPS_EDITOR_FORM_NAME = 'formCustomListConfigMatchGroupsEditor';
const selector = formValueSelector(CUSTOM_LIST_CONFIG_MATCH_GROUPS_EDITOR_FORM_NAME);

interface CustomListConfigMatchGroupsEditorProps {
  version: ReportTemplateVersionCustomList;
  fileWatch: FileWithPreview | null;
  matchRequirementProfiles: MatchRequirementProfile[];
  handleMatchGroupsUpdate: (matchGroups: MatchGroup[]) => void;
}

const CustomListConfigMatchGroupsEditor = ({
  version,
  fileWatch,
  matchRequirementProfiles,
  handleMatchGroupsUpdate,
}: CustomListConfigMatchGroupsEditorProps) => {
  const dispatch = useDispatch();

  const [selectedMatchRequirementKeys, setSelectedMatchRequirementKeys] =
    useState<MatchRequirementKeys | null>(null);

  const EMPTY_MATCH_REQUIREMENT: MatchRequirement = {
    name: undefined,
    description: undefined,
    'attributes-to-compare': [],
    'comparison-methods': [],
    'normalization-methods': [],
  };
  const EMPTY_MATCH_GROUPS = [
    {
      description: 'Match Group',
      name: 'match_group',
      'match-requirements': [EMPTY_MATCH_REQUIREMENT],
    } as MatchGroup,
  ];

  useEffect(() => {
    const initialValues = {
      [version.id]: {
        'match-groups': version.attributes['match-groups'] ?? EMPTY_MATCH_GROUPS,
      },
    };
    dispatch(initialize(CUSTOM_LIST_CONFIG_MATCH_GROUPS_EDITOR_FORM_NAME, initialValues));
  }, [dispatch, version.id, version.attributes['match-groups']]);

  useEffect(() => {
    if (fileWatch) {
      // A defined fileWatch means the user uploaded a new file. In this case,
      // if the new file contains all of the mapped headers from the previously
      // saved config, we preserve the existing match-groups for convenience.
      // Otherwise, we reset them.
      const allHeadersIncluded =
        version &&
        version.attributes['header-mappings'].every(
          (mapping) => fileWatch?.headers?.includes(mapping['header-name']) ?? false,
        );
      if (allHeadersIncluded) {
        dispatch(
          change(
            CUSTOM_LIST_CONFIG_MATCH_GROUPS_EDITOR_FORM_NAME,
            `${version.id}.match-groups`,
            version.attributes['match-groups'] ?? EMPTY_MATCH_GROUPS,
          ),
        );
      } else {
        dispatch(
          change(
            CUSTOM_LIST_CONFIG_MATCH_GROUPS_EDITOR_FORM_NAME,
            `${version.id}.match-groups`,
            EMPTY_MATCH_GROUPS,
          ),
        );
      }
    } else {
      // A undefined fileWatch means that either the customer has not uploaded a file,
      // or they uploaded a new file and then pressed Undo.
      dispatch(
        change(
          CUSTOM_LIST_CONFIG_MATCH_GROUPS_EDITOR_FORM_NAME,
          `${version.id}.match-groups`,
          version.attributes['match-groups'] ?? EMPTY_MATCH_GROUPS,
        ),
      );
    }
    setSelectedMatchRequirementKeys(null);
  }, [fileWatch]);

  // The path in the Redux state is `${version.id}.match-groups` because the formSectionName is version.id
  // and the name of the field that needs to access this state in MatchRequirementPanel is `match-groups.`
  const matchGroups: MatchGroup[] = useSelector<RootState, MatchGroup[]>(
    (state) => selector(state, `${version.id}.match-groups`) ?? [],
  );

  useEffect(() => {
    handleMatchGroupsUpdate(matchGroups);
  }, [matchGroups]);

  const handleAddMatchRequirementToMatchGroup = (matchGroupIndex: number) => {
    const newMatchGroups = [...matchGroups];
    newMatchGroups[matchGroupIndex]['match-requirements'].push(EMPTY_MATCH_REQUIREMENT);

    dispatch(
      change(
        CUSTOM_LIST_CONFIG_MATCH_GROUPS_EDITOR_FORM_NAME,
        `${version.id}.match-groups`,
        newMatchGroups,
      ),
    );
    setSelectedMatchRequirementKeys({
      'match-group-index': matchGroupIndex,
      'match-requirement-index': newMatchGroups[matchGroupIndex]['match-requirements'].length - 1,
      'object-id': version.id,
      scope: version.id,
    });
  };

  const handleAddMatchGroup = (countryCode: CountryCode) => {
    const newMatchGroups = [
      ...matchGroups,
      {
        description: `Match group ${matchGroups.length}`,
        name: `match_group_${matchGroups.length}`,
        'match-requirements': [EMPTY_MATCH_REQUIREMENT],
        'country-codes': countryCode ? [countryCode] : ['default'],
      },
    ];

    dispatch(
      change(
        CUSTOM_LIST_CONFIG_MATCH_GROUPS_EDITOR_FORM_NAME,
        `${version.id}.match-groups`,
        newMatchGroups,
      ),
    );
    setSelectedMatchRequirementKeys({
      'match-group-index': newMatchGroups.length - 1,
      'match-requirement-index': 0,
      'object-id': version.id,
      scope: version.id,
    });
  };

  const handleUpdateMatchGroupName = (matchGroupIndex: number, matchGroupName: string) => {
    const newMatchGroups = matchGroups.map((matchGroup, index) =>
      index === matchGroupIndex ? { ...matchGroup, description: matchGroupName } : matchGroup,
    );

    dispatch(
      change(
        CUSTOM_LIST_CONFIG_MATCH_GROUPS_EDITOR_FORM_NAME,
        `${version.id}.match-groups`,
        newMatchGroups,
      ),
    );
  };

  const handleMatchRequirementSelected = (
    matchGroupIndex: number,
    matchRequirementIndex: number,
  ) => {
    setSelectedMatchRequirementKeys({
      'match-group-index': matchGroupIndex,
      'match-requirement-index': matchRequirementIndex,
      'object-id': version.id,
      scope: version.id,
    });
  };

  const handleDeleteMatchGroup = (matchGroupIndex: number) => {
    // Cannot delete the last one
    if (matchGroups.length <= 1) return;

    const newMatchGroups = matchGroups.filter((_, index) => index !== matchGroupIndex);
    dispatch(
      change(
        CUSTOM_LIST_CONFIG_MATCH_GROUPS_EDITOR_FORM_NAME,
        `${version.id}.match-groups`,
        newMatchGroups,
      ),
    );

    setSelectedMatchRequirementKeys(null);
  };

  return (
    <>
      <MatchRequirementsView
        matchGroups={matchGroups}
        usesMatchGroups={true}
        matchRequirementProfiles={matchRequirementProfiles}
        editable={true}
        selectedMatchRequirement={selectedMatchRequirementKeys}
        handleAddMatchRequirementToMatchGroup={handleAddMatchRequirementToMatchGroup}
        handleAddMatchGroup={handleAddMatchGroup}
        handleDeleteMatchGroup={handleDeleteMatchGroup}
        handleUpdateMatchGroupName={handleUpdateMatchGroupName}
        handleUpdateMatchGroupCountries={undefined} // since showCountryCode is false
        handleMatchRequirementSelected={handleMatchRequirementSelected}
        showCountryCode={false}
      />
      {selectedMatchRequirementKeys && (
        <MatchRequirementPanelWrapper>
          <MatchRequirementPanel
            usesMatchGroups={true}
            matchRequirementProfiles={matchRequirementProfiles}
            // Note: matchRequirementName must follow this format for Redux form data flow
            matchRequirementName={`match-groups.${selectedMatchRequirementKeys['match-group-index']}.match-requirements.${selectedMatchRequirementKeys['match-requirement-index']}`}
            matchRequirement={
              matchGroups[selectedMatchRequirementKeys['match-group-index']]['match-requirements'][
                selectedMatchRequirementKeys['match-requirement-index']
              ]
            }
            matchRequirementKeys={selectedMatchRequirementKeys}
            formSectionName={version.id}
            editable={true}
            handleClose={() => setSelectedMatchRequirementKeys(null)}
          />
        </MatchRequirementPanelWrapper>
      )}
    </>
  );
};

interface CustomListConfigMatchGroupsEditorFormData {
  'match-groups': MatchGroup[];
}

const MatchRequirementPanelWrapper = styled.div`
  position: fixed;
  top: 65px;
  right: 0;
  bottom: 0;
  background-color: ${Color.PrimaryWhite};
  width: 400px;
  max-width: 400px;
  flex: 0 0 400px;
  overflow-y: auto;
  overflow-x: hidden;
  border-left: 1px solid ${Color.BorderDivider};

  & > div {
    height: 100%;
  }
`;

export default reduxForm<
  CustomListConfigMatchGroupsEditorFormData,
  CustomListConfigMatchGroupsEditorProps
>({
  form: CUSTOM_LIST_CONFIG_MATCH_GROUPS_EDITOR_FORM_NAME,
})(CustomListConfigMatchGroupsEditor);
